import { BookInfo, IBookSponsorPointer } from '../book';

import { IProductIssueInfo } from './issue.info';
import { IProductPublicationInfo } from './publication.info';
import { IProductSubscriptionTerm } from './subscription.term';
import { ProductType } from './types';

interface IProduct {
    token: string;
    type: ProductType;

    title: string;

    callToAction: string;
    highlight: string;
    features: string[];

    tags: string[];

    currency: string;
    price: number;
    formattedPrice: string;
    discount: number;
    formattedDiscount: string;
    total: number;
    formattedTotal: string;

    issueIncluded: boolean;

    /** Set at mapping time by {@link isProductTrial}. Never re-derive it from `price` / `autoRenewToProduct`. */
    isTrial: boolean;
}

interface IProductRegularSubscription extends IProduct {
    type: 'RegularSubscription';

    productId: string;
    term: IProductSubscriptionTerm;
    autoRenewEnabled: boolean;
    autoRenewToProduct: IProductRegularSubscription;
}

interface IProductPublicationSubscription extends IProduct {
    type: 'PublicationSubscription';

    productId: string;
    term: IProductSubscriptionTerm;
    autoRenewEnabled: boolean;
    autoRenewToProduct: IProductPublicationSubscription;

    cids: string[];

    publicationsInfo: IProductPublicationInfo[];
}

interface IProductSingleIssue extends IProduct {
    type: 'SingleIssue';

    issueKey: string;
    cid: string;

    issueInfo: IProductIssueInfo | null;
}

interface IProductGiftCard extends IProduct {
    type: 'GiftCard';

    productId: string;
}

enum SupplyModel {
    Unknown = 0,
    Agency = 1,
    Wholesale = 2,
    Free = 3,
    Consignment = 4,
    License = 5,
}

interface IProductBook extends IProduct {
    type: 'Book';

    bookId: number;
    productId: string;
    sponsor?: IBookSponsorPointer;

    autoRenewEnabled: boolean;
    salesModelId: number;
    supplyModelId: SupplyModel;

    bookInfo: BookInfo | null;
}

type AnyProduct = IProductRegularSubscription | IProductPublicationSubscription | IProductSingleIssue | IProductGiftCard | IProductBook;

function isProductSingleIssue(product: AnyProduct): product is IProductSingleIssue {
    return product.type === 'SingleIssue';
}

function isProductRegularSubscription(product: AnyProduct): product is IProductRegularSubscription {
    return product.type === 'RegularSubscription';
}

function isProductPublicationSubscription(product: AnyProduct): product is IProductPublicationSubscription {
    return product.type === 'PublicationSubscription';
}

function isProductBook(product: AnyProduct): product is IProductBook {
    return product.type === 'Book';
}

/** Structural so the one rule runs on both the product DTOs and the mapped models. */
interface ProductTrialInput {
    type: string;
    price: number;
    autoRenewToProduct?: unknown;
}

/**
 * Trial subscription: a zero-priced subscription whose `autoRenewToProduct` points
 * to the real (paid) plan it converts to after the trial period ends.
 *
 * Call from mappers only; everything downstream reads the `isTrial` field.
 */
function isProductTrial(product: ProductTrialInput): boolean {
    if (product.type !== 'RegularSubscription' && product.type !== 'PublicationSubscription') {
        return false;
    }
    return !product.price && product.autoRenewToProduct != null;
}

export {
    AnyProduct,
    IProductBook,
    IProductGiftCard,
    IProductPublicationSubscription,
    IProductRegularSubscription,
    IProductSingleIssue,
    isProductBook,
    isProductPublicationSubscription,
    isProductRegularSubscription,
    isProductSingleIssue,
    isProductTrial,
    SupplyModel,
};

export { IProductIssueInfo, IProductPublicationInfo };

export * from './subscription.term';
export * from './types';
